// Cliente del Device Flow OAuth. Llama a /api/oauth/* (proxies en Pages Functions).
// La complejidad está en el polling — GitHub puede responder authorization_pending
// muchas veces antes de devolver el token.

export interface DeviceCodeResponse {
  device_code: string
  user_code: string
  verification_uri: string
  verification_uri_complete?: string
  expires_in: number
  interval: number
}

export interface AccessTokenResponse {
  access_token: string
  token_type: string
  scope: string
}

export class DeviceFlowError extends Error {
  constructor(public code: string, message?: string) {
    super(message ?? code)
    this.name = 'DeviceFlowError'
  }
}

export async function requestDeviceCode(scope = 'repo read:org'): Promise<DeviceCodeResponse> {
  const res = await fetch('/api/oauth/device-code', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ scope }),
  })
  if (!res.ok) {
    const err = await safeJson(res)
    const code = typeof err?.error === 'string' ? err.error : 'http_error'
    const desc = typeof err?.error_description === 'string' ? err.error_description : `${res.status}`
    throw new DeviceFlowError(code, desc)
  }
  return res.json()
}

// Polea cada `intervalSec` segundos. Resuelve con el token al éxito. Rechaza con
// DeviceFlowError si el código expira, se rechaza, o hay error de red.
// Si se pasa `signal`, el polling se detiene cuando el AbortSignal aborta.
export async function pollForAccessToken(
  deviceCode: string,
  initialIntervalSec: number,
  signal?: AbortSignal,
): Promise<AccessTokenResponse> {
  let intervalMs = initialIntervalSec * 1000

  while (true) {
    if (signal?.aborted) throw new DeviceFlowError('aborted', 'polling cancelado')
    await sleep(intervalMs, signal)

    const res = await fetch('/api/oauth/access-token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ device_code: deviceCode }),
    })
    const data = await safeJson(res)
    if (!data) throw new DeviceFlowError('http_error', `status ${res.status}`)

    if ('access_token' in data && typeof data.access_token === 'string') {
      return data as unknown as AccessTokenResponse
    }

    const code = (data as { error?: string }).error
    if (code === 'authorization_pending') {
      continue
    }
    if (code === 'slow_down') {
      // GitHub pide aumentar el intervalo. Sumamos 5s al actual.
      intervalMs += 5_000
      continue
    }
    // expired_token / access_denied / unsupported_grant_type / etc.
    throw new DeviceFlowError(code ?? 'unknown', (data as { error_description?: string }).error_description)
  }
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(new DeviceFlowError('aborted'))
    const t = setTimeout(resolve, ms)
    signal?.addEventListener('abort', () => {
      clearTimeout(t)
      reject(new DeviceFlowError('aborted'))
    })
  })
}

async function safeJson(res: Response): Promise<Record<string, unknown> | null> {
  try {
    return (await res.json()) as Record<string, unknown>
  } catch {
    return null
  }
}
